import React, { useState, useEffect, useRef } from 'react';
import { IconButton, Paper, Popper } from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import CloseIcon from '@mui/icons-material/Close';

type HelpPopoverButtonProps = {
    renderContent: (pinned: boolean) => React.ReactNode;
    ariaLabel?: string;
};

export const HelpPopoverButton = ({ renderContent, ariaLabel = 'Help' }: HelpPopoverButtonProps) => {
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const [pinned, setPinned] = useState(false);
    const paperRef = useRef<HTMLDivElement>(null);
    const buttonRef = useRef<HTMLButtonElement>(null);

    const open = Boolean(anchorEl);

    const close = () => {
        setAnchorEl(null);
        setPinned(false);
    };

    // Manual click-away detection: closes the popover on any click outside it while
    // pinned. The trigger button is excluded from the "outside" check so its own
    // onClick handler (below) is the single source of truth for toggling — otherwise
    // this listener would close the popover a frame before the click handler re-pins
    // it. This also sidesteps a known MUI ClickAwayListener reliability issue in this
    // project's jsdom/RTL test setup.
    useEffect(() => {
        if (!pinned) return;

        const handleMouseDown = (event: MouseEvent) => {
            const target = event.target as Node;
            if (
                paperRef.current &&
                !paperRef.current.contains(target) &&
                buttonRef.current &&
                !buttonRef.current.contains(target)
            ) {
                close();
            }
        };

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                close();
            }
        };

        document.addEventListener('mousedown', handleMouseDown);
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('mousedown', handleMouseDown);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [pinned]);

    return (
        <>
            <IconButton
                ref={buttonRef}
                size="small"
                aria-label={ariaLabel}
                onMouseEnter={e => {
                    if (!pinned) setAnchorEl(e.currentTarget);
                }}
                onMouseLeave={() => {
                    if (!pinned) setAnchorEl(null);
                }}
                onClick={e => {
                    if (pinned) {
                        close();
                    } else {
                        setPinned(true);
                        setAnchorEl(e.currentTarget);
                    }
                }}
            >
                <HelpOutlineIcon fontSize="small" />
            </IconButton>
            <Popper open={open} anchorEl={anchorEl} placement="bottom-start" sx={{ zIndex: 1500 }}>
                <Paper
                    ref={paperRef}
                    elevation={4}
                    sx={{
                        p: 1.5,
                        maxWidth: 360,
                        pointerEvents: pinned ? 'auto' : 'none',
                        userSelect: pinned ? 'text' : 'none',
                    }}
                >
                    {pinned && (
                        <IconButton
                            size="small"
                            aria-label="Close"
                            onClick={close}
                            sx={{ float: 'right', mt: -0.5, mr: -0.5 }}
                        >
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    )}
                    {renderContent(pinned)}
                </Paper>
            </Popper>
        </>
    );
};
